import { useEffect, useState } from 'react';

/**
 * Mantém o nó montado enquanto a animação de saída roda.
 *
 * Todo overlay do app era `if (!isOpen) return null` — ou seja, sumia no mesmo
 * frame e não havia como animar a saída. Este hook segura a montagem por
 * `saidaMs` depois do fechamento e devolve o estado para a CSS ler:
 *
 *     const { montado, estado } = usePresence(isOpen, 200);
 *     if (!montado) return null;
 *     <div data-state={estado}
 *          className="data-[state=open]:animate-sheet-in
 *                     data-[state=closed]:animate-sheet-out" />
 *
 * Reabrir antes do prazo cancela a remoção (o cleanup limpa o timer), então
 * abrir/fechar rápido não deixa nó preso nem corta a entrada.
 */
export default function usePresence(aberto: boolean, saidaMs = 200) {
  const [montado, setMontado] = useState(aberto);

  useEffect(() => {
    if (aberto) {
      setMontado(true);
      return;
    }
    const timer = setTimeout(() => setMontado(false), saidaMs);
    return () => clearTimeout(timer);
  }, [aberto, saidaMs]);

  return { montado, estado: (aberto ? 'open' : 'closed') as 'open' | 'closed' };
}

/**
 * Guarda o último valor não-nulo.
 *
 * Overlay que é aberto por um dado (`product`, `photo`) e não por um booleano
 * fica sem nada para renderizar no instante em que o dado vira null — que é
 * exatamente quando a animação de saída precisa acontecer. Aqui o conteúdo
 * antigo sobrevive até o nó sair.
 */
export function useUltimoValor<T>(valor: T | null | undefined): T | null {
  const [ultimo, setUltimo] = useState<T | null>(valor ?? null);

  useEffect(() => {
    if (valor != null) setUltimo(valor);
  }, [valor]);

  return valor ?? ultimo;
}
